import { request, authRequest } from "~/utils/api";

/**
 * 获取指定分类的群组列表
 *
 * @param {number} groupCategoryId - 群组分类的ID
 * @returns {Promise<Array>} 返回包含群组对象的Promise
 */
export const getGroups = (groupCategoryId) => {
  const params = { group_category_id: groupCategoryId };
  return request({ url: "/groups", params: params }).then(
    (res) => res.data.groups
  );
};

/**
 * 获取指定ID的群信息
 *
 * @param {string} id - 群组ID
 * @returns {Promise<Object>} 返回包含群组信息的Promise对象
 */
export const getGroup = (id) =>
  request({ url: `/groups/${id}` }).then((res) => res.data);

/**
 * 获取群组分类列表
 *
 * @returns {Promise<Array>} 群组分类列表
 */
export const getGroupCategories = () =>
  request({ url: "/group_categories" }).then(
    (res) => res.data.group_categories
  );

/**
 * 创建群组申请
 *
 * @param {String} token 用户 token
 * @param {FormData} formData 群组申请数据
 * @returns {Promise<Object>} 创建的群组申请对象
 */
export const createGroupApplication = (token, formData) =>
  authRequest(token, {
    url: "/groups/managed_groups",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).then((res) => res.data.data);

/**
 * 获取用户管理的群组
 *
 * @param {String} token 用户 token
 * @returns {Promise<Array>} 用户管理的群组列表
 */
export const getManagedGroups = (token) =>
  authRequest(token, { url: "/groups/managed_groups" }).then(
    (res) => res.data.data
  );

/**
 * 获取用户管理的指定群组
 *
 * @param {String} token 用户 token
 * @param {String} id 用户管理的群组ID
 * @returns {Object} 群组对象
 */
export const getManagedGroup = (token, id) =>
  authRequest(token, { url: `/groups/managed_groups/${id}` }).then(
    (res) => res.data.data
  );

/**
 * 更新用户管理的指定群组
 *
 * @param {String} token 用户 token
 * @param {String} id 用户管理的群组ID
 * @param {Object} formData 更新的群组数据
 * @returns {Object} 更新后的群组对象
 */
export const updateManagedGroup = (token, id, formData) =>
  authRequest(token, {
    url: `/groups/managed_groups/${id}`,
    method: "PATCH",
    data: formData,
  }).then((res) => res.data.data);

/**
 * 获取群组的微信推送邮箱设置
 *
 * @param {string} token - 用户 token
 * @returns {Promise<Object>} 返回包含微信推文设置的Promise对象
 */
export const getGroupWechatPostSetting = (token) =>
  authRequest(token, { url: "/groups/wechat_post_setting" }).then(
    (res) => res.data.data
  );

/**
 * 更新微信推送邮箱设置
 *
 * @param {String} token 用户 token
 * @param {object} data 邮件设定
 * @param {String} data.email 邮箱地址
 * @param {String} data.token 6为数字验证码
 * @returns {Promise<Object>} 返回包含微信推文设置的Promise对象
 */
export const updateGroupWechatPostSetting = (token, data) =>
  authRequest(token, {
    url: "/groups/wechat_post_setting",
    method: "POST",
    data: data,
  }).then((res) => res.data.data);

/**
 * 获取群组帖子列表
 *
 * @param {string} token - 用户认证令牌
 * @param {number} page - 页码
 * @returns {Promise<Object>} 包含群组帖子的响应数据
 */
export const getWechatGroupPosts = (token, page = 1) =>
  authRequest(token, { url: "/groups/wechat_posts", params: { page } }).then(
    (res) => res.data
  );

/**
 * 检查用户是否已申请过群组信息
 *
 * @param {string} token - 用户认证令牌
 * @param {string} id - 群组ID
 * @returns {Promise<boolean>} 返回用户是否已申请过群组信息
 */
export const getGroupApply = (token, id) =>
  authRequest(token, { url: `/groups/${id}/apply` }).then(
    (res) => res.data.data
  );

/**
 * 申请查看群组信息
 *
 * @param {string} token - 用户认证令牌
 * @param {string} id - 群组ID
 * @returns {Promise<boolean>} 返回用户是否已申请过群组信息
 */
export const createGroupApply = (token, id) =>
  authRequest(token, { url: `/groups/${id}/apply`, method: "POST" }).then(
    (res) => res.data.data
  );

/**
 * 获取管理员群组列表
 *
 * @param {string} token - 用户认证令牌
 * @param {object} params - 查询参数
 * @param {string} params.status - 状态过滤 (pending, rejected, approved)
 * @param {number} params.page - 页码
 * @param {number} params.per_page - 每页数量
 * @returns {Promise<Object>} 返回群组列表和分页信息
 */
export const getAdminGroups = (token, params = {}) =>
  authRequest(token, {
    url: "/admin/groups",
    method: "GET",
    params,
  }).then((res) => res.data);

/**
 * 批准群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @returns {Promise<Object>} 返回操作结果
 */
export const approveAdminGroup = (token, groupId) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/approve`,
    method: "POST",
  }).then((res) => res.data);

/**
 * 拒绝群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @param {string} remark - 拒绝理由
 * @returns {Promise<Object>} 返回操作结果
 */
export const rejectAdminGroup = (token, groupId, remark = "") =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/reject`,
    method: "POST",
    data: { remark },
  }).then((res) => res.data);

/**
 * 禁用群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @returns {Promise<Object>} 返回操作结果
 */
export const disableAdminGroup = (token, groupId) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/disable`,
    method: "POST",
  }).then((res) => res.data);

/**
 * 启用群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @returns {Promise<Object>} 返回操作结果
 */
export const enableAdminGroup = (token, groupId) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/enable`,
    method: "POST",
  }).then((res) => res.data);

/**
 * 置顶或取消置顶群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @param {boolean} pinned - 是否置顶
 * @returns {Promise<Object>} 返回操作结果
 */
export const togglePinnedAdminGroup = (token, groupId, pinned) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/set_pinned`,
    method: "POST",
    data: { pinned },
  }).then((res) => res.data);

/**
 * 设置新群或取消新群群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @param {boolean} recent - 是否新群
 * @returns {Promise<Object>} 返回操作结果
 */
export const toggleRecentAdminGroup = (token, groupId, recent) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/set_recent`,
    method: "POST",
    data: { recent },
  }).then((res) => res.data);

/**
 * 加精或取消加精群组
 *
 * @param {string} token - 用户认证令牌
 * @param {string|number} groupId - 群组ID
 * @param {boolean} featured - 是否加精
 * @returns {Promise<Object>} 返回操作结果
 */
export const toggleFeaturedAdminGroup = (token, groupId, featured) =>
  authRequest(token, {
    url: `/admin/groups/${groupId}/status_actions/set_featured`,
    method: "POST",
    data: { featured },
  }).then((res) => res.data);
